'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Determine current language - English, French, and Arabic
  // Use safe defaults during initial render to prevent hydration errors
  const isFrench = mounted && (pathname?.includes('-french') || pathname?.includes('/propos')) || false;
  const isArabic = mounted && pathname?.startsWith('/ar') || false;

  // Navigation links based on language
  const getNavigationLinks = () => {
    if (isArabic) {
      return [
        { label: 'الرئيسية', href: '/ar' },
        { label: 'من نحن', href: '/ar/about' },
        { label: 'الخدمات', href: '/ar/services' },
        { label: 'المنهجية', href: '/ar/approach' },
        { label: 'آراء العملاء', href: '/ar/insights' },
        { label: 'اتصل بنا', href: '/ar/contact' }
      ];
    } else if (isFrench) {
      return [
        { label: 'ACCUEIL', href: '/homepage-french' },
        { label: 'SERVICES', href: '/practice-french' },
        { label: 'APPROCHE', href: '/approach-french' },
        { label: 'LA FONDATRICE', href: '/propos' },
        { label: 'AVIS', href: '/selected-professional-feedback-french' },
        { label: 'CONTACT', href: '/contact-french' }
      ];
    } else {
      return [
        { label: 'Home', href: '/homepage' },
        { label: 'Services', href: '/practice' },
        { label: 'Approach', href: '/approach' },
        { label: 'The Founder', href: '/about' },
        { label: 'Feedback', href: '/selected-professional-feedback' },
        { label: 'Contact', href: '/contact' }
      ];
    }
  };

  const navigationLinks = getNavigationLinks();

  // Language switcher logic - English, French, and Arabic
  const getCurrentLanguageCode = () => {
    if (isArabic) return 'ar';
    if (isFrench) return 'fr';
    return 'en';
  };

  const getLanguageSwitchPath = (targetLang: string) => {
    // Get base path without language suffixes
    let basePath = pathname?.replace('-french', '').replace('-ar', '').replace('-arabic', '').replace('/propos', '/about') || '/homepage';
    
    // Remove /ar prefix if present
    if (basePath.startsWith('/ar/')) {
      basePath = '/' + basePath.substring(4);
    } else if (basePath === '/ar') {
      basePath = '/homepage';
    }
    
    // Handle root path
    if (basePath === '/' || basePath === '') {
      basePath = '/homepage';
    }
    
    // Return appropriate path based on target language
    if (targetLang === 'ar') {
      // Arabic placeholder page
      return '/ar';
    } else if (targetLang === 'fr') {
      return `${basePath}-french`;
    } else {
      return basePath;
    }
  };

  const languageOptions = [
    { code: 'en', label: 'English', nativeLabel: 'EN' },
    { code: 'fr', label: 'Français', nativeLabel: 'FR' },
    { code: 'ar', label: 'العربية', nativeLabel: 'AR' }
  ];

  const currentLanguage = languageOptions.find((lang) => lang.code === getCurrentLanguageCode());

  return (
    <header className="w-full bg-background/95 backdrop-blur-sm border-b border-border" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-7xl mx-auto px-xl">
        <div 
          className="flex items-center justify-between h-[135px] min-h-[135px] max-h-[135px] pt-0 pb-0 mt-0 mb-0"
          style={isArabic ? { direction: 'ltr' } : {}}
        >
          {/* Logo */}
          <Link href={isArabic ? '/ar' : isFrench ? '/homepage-french' : '/homepage'} className="group">
            <div className="flex flex-col items-start">
              <Image
                src="/assets/images/ChatGPT_Image_31_janv._2026_12_23_55-1769859035755.png"
                alt="ND Law Logo"
                width={700}
                height={224}
                className="w-auto object-contain transition-opacity duration-base group-hover:opacity-80"
                priority
                style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.05))' }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-lg" style={isArabic ? { direction: 'ltr' } : {}}>
            {navigationLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-light tracking-wide hover:text-accent transition-colors duration-base uppercase"
                style={{ color: '#0B2F2F' }}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Switcher - Inline text-only for all versions */}
            <div className="flex items-center gap-md ml-md">
              <Link
                href={getLanguageSwitchPath('en')}
                className={`px-sm py-xs text-xs font-medium tracking-wide transition-all duration-base uppercase border ${
                  getCurrentLanguageCode() === 'en' ?'border-accent text-accent bg-accent/5' :'border-border hover:border-accent hover:text-accent'
                }`}
                style={getCurrentLanguageCode() === 'en' ? {} : { color: '#0B2F2F' }}
                title="English"
              >
                ENG
              </Link>
              <Link
                href={getLanguageSwitchPath('fr')}
                className={`px-sm py-xs text-xs font-medium tracking-wide transition-all duration-base uppercase border ${
                  getCurrentLanguageCode() === 'fr' ?'border-accent text-accent bg-accent/5' :'border-border hover:border-accent hover:text-accent'
                }`}
                style={getCurrentLanguageCode() === 'fr' ? {} : { color: '#0B2F2F' }}
                title="Français"
              >
                FR
              </Link>
              <Link
                href={getLanguageSwitchPath('ar')}
                className={`px-sm py-xs text-xs font-medium tracking-wide transition-all duration-base uppercase border ${
                  getCurrentLanguageCode() === 'ar' ?'border-accent text-accent bg-accent/5' :'border-border hover:border-accent hover:text-accent'
                }`}
                style={getCurrentLanguageCode() === 'ar' ? {} : { color: '#0B2F2F' }}
                title="العربية"
              >
                AR
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-primary hover:text-accent transition-colors duration-base"
            aria-label="Toggle mobile menu"
            aria-expanded={isMenuOpen}
          >
            <Icon
              name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border bg-background">
            <nav className="flex flex-col py-md px-xl space-y-sm">
              {navigationLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-base font-normal tracking-wide hover:text-accent transition-colors duration-base py-sm"
                  style={{ color: '#0B2F2F' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="border-t border-border mt-sm pt-md space-y-sm">
                {languageOptions.map((lang) => (
                  <Link
                    key={lang.code}
                    href={getLanguageSwitchPath(lang.code)}
                    className={`block text-base font-medium tracking-wide hover:text-accent transition-colors duration-base py-sm ${
                      lang.code === getCurrentLanguageCode() ? 'text-accent' : ''
                    }`}
                    style={{ color: lang.code === getCurrentLanguageCode() ? undefined : '#0B2F2F' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {lang.label}
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;